import {Component, OnInit} from '@angular/core';
import {HeaderComponent} from '../header/header.component';
import {FooterComponent} from '../footer/footer.component';
import {CommonModule} from '@angular/common';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';

// Angular Material
import {MatTableModule} from '@angular/material/table';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatCardModule} from '@angular/material/card';
import {MatTooltipModule} from '@angular/material/tooltip';
import {DeleteDialogComponent} from '../delete/delete-dialog.component';
import {ScheduleDialogComponent} from './schedule-dialog.component';
import {ScheduleValidationDialogComponent} from './schedule-validation-dialog.component';
import {VersionPanelComponent} from './version-panel.component';
import {ExportPdfDialogComponent, PdfExportOptions} from './export-pdf-dialog.component';

interface Schedule {
  id: string;
  name: string;
  [key: string]: any;
}

@Component({
  selector: 'app-schedules',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
  ],
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss'],
})
export class SchedulesComponent implements OnInit {
  schedules: Schedule[] = [];
  loading = false;
  error: string | null = null;
  scheduleResourceTypes: Map<string, string[]> = new Map();
  scheduleRequiredResourceTypes: Map<string, string[]> = new Map();
  resourceTypeNameMap: Map<number | string, string> = new Map();
  versionNameMap: Map<string, string> = new Map();
  versionNumberMap: Map<string, number> = new Map();

  // Track which schedules are being recreated (allow multiple at once)
  recreatingScheduleIds: Set<string> = new Set();

  // Track which schedules are being redeployed
  redeployingScheduleIds: Set<string> = new Set();

  // Track which schedules are being exported
  exportingScheduleIds: Set<string> = new Set();

  // Track which schedules are being exported to PDF
  exportingPdfScheduleIds: Set<string> = new Set();

  displayedColumns: string[] = ['id', 'name', 'versionPill', 'currentVersion', 'resourceTypes', 'requiredResourceTypes', 'primaryResourceType', 'actions'];

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.error = 'No organisation selected.';
      return;
    }
    this.organisationId = storedId;
    this.fetchSchedules();
  }

  fetchSchedules() {
    this.loading = true;
    this.scheduleResourceTypes.clear();
    this.scheduleRequiredResourceTypes.clear();
    this.resourceTypeNameMap.clear();

    // Fetch all resource types for the org (for mapping id to name)
    this.http
      .get<any[]>(`${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`)
      .subscribe({
        next: (resourceTypes) => {
          for (const rt of resourceTypes) {
            this.resourceTypeNameMap.set(rt.id.toString(), rt.name);
          }

          this.http
            .get<Schedule[]>(
              `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules`
            )
            .subscribe({
              next: (data) => {
                this.schedules = data;

                // Fetch version names for all currentVersionIds
                const versionIds = data
                  .map(s => s['currentVersionId'])
                  .filter((id, idx, arr) => !!id && arr.indexOf(id) === idx);
                if (versionIds.length > 0) {
                  // Use the correct AJAX call to get all currentVersionIds
                  this.http
                    .get<any[]>(`${this.apiBaseUrl}/scheduleversion?ids=${versionIds.join(',')}`)
                    .subscribe({
                      next: (versions) => {
                        // If the endpoint returns a single object, wrap it in an array for compatibility
                        const versionArr = Array.isArray(versions) ? versions : [versions];
                        for (const v of versionArr) {
                          this.versionNameMap.set(v.id.toString(), v.name);
                          this.versionNumberMap.set(v.id.toString(), v.versionNumber);
                        }
                      },
                      error: (err) => {
                        console.error('Failed to load version names', err);
                      }
                    });
                }

                // Fetch resource types for each schedule
                const promises = data.map((schedule) =>
                  this.fetchResourceTypesForSchedule(schedule.id)
                );

                Promise.all(promises).finally(() => {
                  this.loading = false;
                });
              },
              error: (err: HttpErrorResponse) => {
                this.error = err.message;
                this.loading = false;
                this.snackBar.open('Failed to load schedules', 'Close', {
                  duration: 3000,
                });
              },
            });
        },
        error: (err) => {
          this.error = 'Failed to load resource types for mapping';
          this.loading = false;
        }
      });
  }

  fetchResourceTypesForSchedule(scheduleId: string) {
    return new Promise<void>((resolve) => {
      this.http
        .get<any[]>(`${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes`)
        .subscribe({
          next: async (links) => {
            if (links.length > 0) {
              // Get resource type details for each link
              const resourceTypeIds = links.map((link) => link.resourceTypeId);
              // Filter for required resource types
              const requiredResourceTypeIds = links
                .filter((link) => link.required !== false) // Default to true if undefined
                .map((link) => link.resourceTypeId);

              try {
                const resourceTypes = await this.http
                  .get<any[]>(
                    `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`
                  )
                  .toPromise();

                if (resourceTypes) {
                  // Filter to only the resource types associated with this schedule
                  const matchingTypes = resourceTypes
                    .filter((rt) => resourceTypeIds.includes(rt.id))
                    .map((rt) => rt.name);

                  const requiredMatchingTypes = resourceTypes
                    .filter((rt) => requiredResourceTypeIds.includes(rt.id))
                    .map((rt) => rt.name);

                  this.scheduleResourceTypes.set(scheduleId, matchingTypes);
                  this.scheduleRequiredResourceTypes.set(scheduleId, requiredMatchingTypes);
                }
              } catch (error) {
                console.error('Error fetching resource type details', error);
              }
            } else {
              this.scheduleResourceTypes.set(scheduleId, []);
              this.scheduleRequiredResourceTypes.set(scheduleId, []);
            }
            resolve();
          },
          error: (err) => {
            console.error('Failed to load resource types for schedule', err);
            this.scheduleResourceTypes.set(scheduleId, []);
            this.scheduleRequiredResourceTypes.set(scheduleId, []);
            resolve();
          },
        });
    });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(ScheduleDialogComponent, {
      width: '400px',
      data: { name: '' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.createSchedule(result);
      }
    });
  }

  onRowClick(event: MouseEvent, schedule: Schedule) {
    // Don't open the dialog if the click was on a button in the actions column
    if (!(event.target as HTMLElement).closest('.mat-column-actions')) {
      this.openEditDialog(schedule);
    }
  }

  navigateToConstraints(schedule: Schedule, event: MouseEvent) {
    // Prevent row click event from firing
    event.stopPropagation();
    this.router.navigate(['/schedules', schedule.id, 'constraints']);
  }

  openEditDialog(schedule: Schedule) {
    // Fetch all versions for this schedule before opening the dialog
    this.http
      .get<any[]>(`${this.apiBaseUrl}/schedules/${schedule.id}/versions`)
      .subscribe({
        next: (versions) => {
          const dialogRef = this.dialog.open(ScheduleDialogComponent, {
            width: '520px',
            data: { ...schedule, allVersions: versions },
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result && result.name) {
              // If user changed the currentVersionId, update it
              if (
                typeof result.currentVersionId !== 'undefined' &&
                result.currentVersionId !== schedule['currentVersionId']
              ) {
                this.updateCurrentVersion(schedule.id, result.currentVersionId, () => {
                  this.updateSchedule(schedule, result);
                });
              } else {
                this.updateSchedule(schedule, result);
              }
            }
          });
        },
        error: (err) => {
          this.snackBar.open('Failed to load schedule versions', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateCurrentVersion(scheduleId: string, versionId: number, cb?: () => void) {
    this.http
      .put(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${scheduleId}`,
        { currentVersionId: versionId }
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Current version updated', 'Close', {
            duration: 2000,
          });
          if (cb) cb();
          this.fetchSchedules();
        },
        error: (err) => {
          this.snackBar.open('Failed to update current version', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  createSchedule(newSchedule: Partial<Schedule>) {
    // Extract selectedResourceTypes, resourceTypeSelections, and primaryResourceTypeId before sending to API
    const { selectedResourceTypes, resourceTypeSelections, primaryResourceTypeId, ...scheduleData } = newSchedule as any;

    // Add organizationId and primaryResourceTypeId to the schedule data if present
    const scheduleToCreate: any = {
      ...scheduleData,
      organizationId: parseInt(this.organisationId),
    };
    if (primaryResourceTypeId) {
      scheduleToCreate.primaryResourceTypeId = primaryResourceTypeId;
    }

    this.http
      .post<Schedule>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules`,
        scheduleToCreate
      )
      .subscribe({
        next: (createdSchedule) => {
          // If resource types were selected, create the associations
          if (resourceTypeSelections && resourceTypeSelections.length > 0) {
            this.addResourceTypesToSchedule(
              createdSchedule.id,
              resourceTypeSelections
            );
          } else if (selectedResourceTypes && selectedResourceTypes.length > 0) {
            // Fallback for backward compatibility
            this.addResourceTypesToSchedule(
              createdSchedule.id,
              selectedResourceTypes.map((rtId: string) => ({
                resourceTypeId: rtId,
                required: true
              }))
            );
          } else {
            this.snackBar.open('Schedule created', 'Close', {
              duration: 2000,
            });
            this.fetchSchedules();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to create schedule', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  addResourceTypesToSchedule(scheduleId: string, resourceTypeSelections: Array<{resourceTypeId: string, required: boolean}>) {
    // Create a counter to track completed requests
    let completed = 0;
    let hasErrors = false;

    // For each resource type, create an association
    resourceTypeSelections.forEach((selection) => {
      this.http
        .post(`${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes`, {
          resourceTypeId: parseInt(selection.resourceTypeId),
          required: selection.required,
        })
        .subscribe({
          next: () => {
            completed++;
            if (completed === resourceTypeSelections.length) {
              this.snackBar.open(
                'Schedule created with resource types',
                'Close',
                {
                  duration: 2000,
                }
              );
              this.fetchSchedules();
            }
          },
          error: (err) => {
            hasErrors = true;
            completed++;
            console.error('Failed to add resource type to schedule', err);
            if (completed === resourceTypeSelections.length) {
              this.snackBar.open(
                'Schedule created but some resource types could not be added',
                'Close',
                {
                  duration: 3000,
                }
              );
              this.fetchSchedules();
            }
          },
        });
    });
  }

  updateSchedule(schedule: Schedule, updatedData: Partial<Schedule>) {
    // Extract selectedResourceTypes and resourceTypeSelections before sending to API
    const { selectedResourceTypes, resourceTypeSelections, ...scheduleData } = updatedData as any;

    // Add organizationId to the schedule data
    const scheduleToUpdate = {
      ...scheduleData,
      organizationId: parseInt(this.organisationId),
    };

    this.http
      .put<Schedule>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${schedule.id}`,
        scheduleToUpdate
      )
      .subscribe({
        next: () => {
          // If resource types were selected, update the associations
          if (resourceTypeSelections) {
            this.updateScheduleResourceTypes(
              schedule.id,
              resourceTypeSelections
            );
          } else if (selectedResourceTypes) {
            // Fallback for backward compatibility
            this.updateScheduleResourceTypes(
              schedule.id,
              selectedResourceTypes.map((rtId: string) => ({
                resourceTypeId: rtId,
                required: true
              }))
            );
          } else {
            this.snackBar.open('Schedule updated', 'Close', {
              duration: 2000,
            });
            this.fetchSchedules();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to update schedule', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateScheduleResourceTypes(
    scheduleId: string,
    resourceTypeSelections: Array<{resourceTypeId: string, required: boolean}>
  ) {
    // First, get existing resource type associations
    this.http
      .get<any[]>(`${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes`)
      .subscribe({
        next: (existingLinks) => {
          const existingIds = existingLinks.map((link) =>
            link.resourceTypeId.toString()
          );
          const existingLinksMap = new Map(
            existingLinks.map((link) => [link.resourceTypeId.toString(), link])
          );

          const newIds = resourceTypeSelections.map(s => s.resourceTypeId);
          const newSelectionsMap = new Map(
            resourceTypeSelections.map(s => [s.resourceTypeId, s])
          );

          // Determine which to add, update, and remove
          const idsToAdd = resourceTypeSelections.filter(
            (selection) => !existingIds.includes(selection.resourceTypeId)
          );
          const linksToRemove = existingLinks.filter(
            (link) => !newIds.includes(link.resourceTypeId.toString())
          );
          const linksToUpdate = existingLinks.filter(
            (link) => {
              const rtId = link.resourceTypeId.toString();
              if (newIds.includes(rtId)) {
                const newSelection = newSelectionsMap.get(rtId);
                // Check if required flag changed
                return newSelection && link.required !== newSelection.required;
              }
              return false;
            }
          );

          // Track completion of all operations
          let totalOperations = idsToAdd.length + linksToRemove.length + linksToUpdate.length;
          let completedOperations = 0;
          let hasErrors = false;

          // If no changes needed
          if (totalOperations === 0) {
            this.snackBar.open('Schedule updated', 'Close', {
              duration: 2000,
            });
            this.fetchSchedules();
            return;
          }

          // Function to check if all operations are complete
          const checkCompletion = () => {
            completedOperations++;
            if (completedOperations === totalOperations) {
              if (hasErrors) {
                this.snackBar.open(
                  'Schedule updated but some resource type changes failed',
                  'Close',
                  {
                    duration: 3000,
                  }
                );
              } else {
                this.snackBar.open(
                  'Schedule updated with resource types',
                  'Close',
                  {
                    duration: 2000,
                  }
                );
              }
              this.fetchSchedules();
            }
          };

          // Add new associations
          idsToAdd.forEach((selection) => {
            this.http
              .post(
                `${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes`,
                {
                  resourceTypeId: parseInt(selection.resourceTypeId),
                  required: selection.required,
                }
              )
              .subscribe({
                next: () => checkCompletion(),
                error: (err) => {
                  hasErrors = true;
                  console.error('Failed to add resource type to schedule', err);
                  checkCompletion();
                },
              });
          });

          // Update existing associations with changed required flag
          linksToUpdate.forEach((link) => {
            const rtId = link.resourceTypeId.toString();
            const newSelection = newSelectionsMap.get(rtId);
            if (newSelection) {
              this.http
                .put(
                  `${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes/${link.id}`,
                  {
                    resourceTypeId: link.resourceTypeId,
                    required: newSelection.required,
                  }
                )
                .subscribe({
                  next: () => checkCompletion(),
                  error: (err) => {
                    hasErrors = true;
                    console.error('Failed to update resource type required flag', err);
                    checkCompletion();
                  },
                });
            }
          });

          // Remove old associations
          linksToRemove.forEach((link) => {
            this.http
              .delete(
                `${this.apiBaseUrl}/schedules/${scheduleId}/resourcetypes/${link.id}`
              )
              .subscribe({
                next: () => checkCompletion(),
                error: (err) => {
                  hasErrors = true;
                  console.error(
                    'Failed to remove resource type from schedule',
                    err
                  );
                  checkCompletion();
                },
              });
          });
        },
        error: (err) => {
          console.error('Failed to fetch existing resource type links', err);
          this.snackBar.open(
            'Schedule updated but failed to update resource types',
            'Close',
            {
              duration: 3000,
            }
          );
          this.fetchSchedules();
        },
      });
  }

  getResourceTypeNames(scheduleId: string): string {
    const types = this.scheduleResourceTypes.get(scheduleId) || [];
    return types.length > 0 ? types.join(', ') : 'None';
  }

  getRequiredResourceTypeNames(scheduleId: string): string {
    const types = this.scheduleRequiredResourceTypes.get(scheduleId) || [];
    return types.length > 0 ? types.join(', ') : 'None';
  }

  getPrimaryResourceTypeName(schedule: Schedule): string {
    if (!schedule['primaryResourceTypeId']) return '—';
    // Use string key for map
    return this.resourceTypeNameMap.get(schedule['primaryResourceTypeId'].toString()) || '—';
  }

  getCurrentVersionName(schedule: Schedule): string {
    if (!schedule['currentVersionId']) return '—';
    return this.versionNameMap.get(schedule['currentVersionId'].toString()) || '—';
  }

  getCurrentVersionNumber(schedule: Schedule): string | number {
    if (!schedule['currentVersionId']) return '—';
    return this.versionNumberMap.get(schedule['currentVersionId'].toString()) ?? schedule['currentVersionId'];
  }

  deleteSchedule(schedule: Schedule) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Schedule',
        message: `Are you sure you want to delete schedule "${schedule.name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${schedule.id}`
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Schedule deleted', 'Close', {
                duration: 2000,
              });
              this.fetchSchedules();
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open('Failed to delete schedule', 'Close', {
                duration: 3000,
              });
            },
          });
      }
    });
  }

  openRecreateDialog(schedule: Schedule, event: MouseEvent) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Recreate Schedule',
        message: `Are you sure you want to recreate the schedule "${schedule.name}"? This will overwrite the current version.`,
        confirmText: 'Recreate',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.recreateSchedule(schedule.id);
      }
    });
  }

  recreateSchedule(scheduleId: string) {
    this.recreatingScheduleIds.add(scheduleId);
    this.snackBar.open('Recreating schedule...', 'Close', { duration: 2000 });
    this.http
      .get<any>(`${this.apiBaseUrl}/generate/${scheduleId}/1`)
      .subscribe({
        next: (data) => {
          this.snackBar.open('Schedule recreated', 'Close', { duration: 2000 });
          this.recreatingScheduleIds.delete(scheduleId);
          this.fetchSchedules();
        },
        error: (err) => {
          this.snackBar.open('Failed to recreate schedule', 'Close', {
            duration: 3000,
          });
          this.recreatingScheduleIds.delete(scheduleId);
        },
      });
  }

  redeployLambda(schedule: Schedule, event: Event) {
    event.stopPropagation();

    const scheduleId = schedule.id;
    const currentVersionId = schedule['currentVersionId'];

    if (!currentVersionId) {
      this.snackBar.open('No current version found', 'Close', { duration: 3000 });
      return;
    }

    const versionNumber = this.versionNumberMap.get(currentVersionId.toString());

    if (!versionNumber) {
      this.snackBar.open('Version number not found', 'Close', { duration: 3000 });
      return;
    }

    this.redeployingScheduleIds.add(scheduleId);
    this.snackBar.open('Redeploying Lambda...', 'Close', { duration: 2000 });

    this.http
      .post<any>(`${this.apiBaseUrl}/schedules/${scheduleId}/versions/${versionNumber}/redeploy-lambda`, {})
      .subscribe({
        next: (data) => {
          this.snackBar.open('Lambda redeployed successfully', 'Close', { duration: 2000 });
          this.redeployingScheduleIds.delete(scheduleId);
        },
        error: (err) => {
          this.snackBar.open('Failed to redeploy Lambda: ' + (err.error?.error || 'Unknown error'), 'Close', {
            duration: 5000,
          });
          this.redeployingScheduleIds.delete(scheduleId);
        },
      });
  }

  getExportTooltip(schedule: Schedule): string {
    if (this.exportingScheduleIds.has(schedule.id)) {
      return 'Exporting...';
    }

    if (!schedule['externalKey']) {
      return 'Not imported from AllUnited - cannot export';
    }

    if (!schedule['itemsCount'] || schedule['itemsCount'] === 0) {
      return 'No sessions - generate schedule first';
    }

    return 'Export to AllUnited';
  }

  getPdfExportTooltip(schedule: Schedule): string {
    if (this.exportingPdfScheduleIds.has(schedule.id)) {
      return 'Generating PDF...';
    }

    if (!schedule['itemsCount'] || schedule['itemsCount'] === 0) {
      return 'No sessions - generate schedule first';
    }

    return 'Export to PDF';
  }

  openPdfExportDialog(schedule: Schedule, event: Event) {
    event.stopPropagation();

    if (!schedule['itemsCount'] || schedule['itemsCount'] === 0) {
      this.snackBar.open('Schedule has no sessions to export', 'Close', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ExportPdfDialogComponent, {
      width: '400px',
      data: { scheduleName: schedule.name }
    });

    dialogRef.afterClosed().subscribe((options: PdfExportOptions | undefined) => {
      if (options) {
        this.exportScheduleToPdf(schedule, options);
      }
    });
  }

  private exportScheduleToPdf(schedule: Schedule, options: PdfExportOptions) {
    const scheduleId = schedule.id;
    this.exportingPdfScheduleIds.add(scheduleId);

    this.http.post(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${scheduleId}/export-pdf`,
      options,
      { responseType: 'blob' }
    ).subscribe({
      next: (blob: Blob) => {
        this.exportingPdfScheduleIds.delete(scheduleId);

        const filename = `${schedule.name}-${options.viewMode}.pdf`.replace(/[^a-zA-Z0-9-_.]/g, '_');
        const url = window.URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } finally {
          window.URL.revokeObjectURL(url);
        }

        this.snackBar.open('PDF exported successfully', 'Close', { duration: 3000 });
      },
      error: (err: HttpErrorResponse) => {
        this.exportingPdfScheduleIds.delete(scheduleId);
        console.error('PDF export failed:', err);
        this.snackBar.open('Failed to export PDF: ' + (err.error?.error || err.message), 'Close', { duration: 5000 });
      }
    });
  }

  exportSchedule(schedule: Schedule, event: Event) {
    event.stopPropagation();

    const scheduleId = schedule.id;

    if (!schedule['externalKey']) {
      this.snackBar.open('Schedule does not have an external key', 'Close', { duration: 3000 });
      return;
    }

    if (!schedule['itemsCount'] || schedule['itemsCount'] === 0) {
      this.snackBar.open('Schedule has no sessions to export', 'Close', { duration: 3000 });
      return;
    }

    this.exportingScheduleIds.add(scheduleId);
    this.snackBar.open('Exporting to AllUnited...', 'Close', { duration: 2000 });

    // Get AllUnited access token from localStorage and send in body
    const allunitedToken = localStorage.getItem('allunited_access_token');
    console.log('AllUnited token from localStorage:', allunitedToken);

    this.http
      .post<any>(`${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${scheduleId}/export`,
        { allunitedAccessToken: allunitedToken }
      )
      .subscribe({
        next: (response) => {
          const message = response.sessionsExported
            ? `Exported ${response.sessionsExported} sessions to AllUnited`
            : 'Schedule exported successfully';
          this.snackBar.open(message, 'Close', { duration: 3000 });
          this.exportingScheduleIds.delete(scheduleId);
        },
        error: (err) => {
          const errorMessage = err.error?.message || err.error?.error || 'Unknown error';
          this.snackBar.open('Export failed: ' + errorMessage, 'Close', {
            duration: 5000,
          });
          this.exportingScheduleIds.delete(scheduleId);
        },
      });
  }

  validateSchedule(schedule: Schedule, event: Event) {
    event.stopPropagation();

    this.dialog.open(ScheduleValidationDialogComponent, {
      width: '700px',
      maxHeight: '80vh',
      data: {
        scheduleId: parseInt(schedule.id),
        organisationId: parseInt(this.organisationId)
      }
    });
  }

  openVersionPanel(schedule: Schedule, event: Event) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(VersionPanelComponent, {
      width: '700px',
      maxHeight: '85vh',
      data: {
        scheduleId: parseInt(schedule.id),
        scheduleName: schedule.name
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === 'reverted') {
        // Refresh schedules to show updated version
        this.fetchSchedules();
      }
    });
  }
}
